import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  buscarAlertas,
  buscarEmpresasAlertas,
  reagendarNotificacao,
} from "./api";
import type {
  AlertaHistorico,
  EmpresaAlertas,
  FiltrosAlertas,
  ReagendarResposta,
} from "./tipos";

/*
 * Hooks de server-state do historico de alertas (FE-7 — ADR 0026 §Estado /
 * TanStack Query). Toda leitura/escrita passa pelo Query: cache, dedup e
 * revalidacao com a config conservadora do `criarQueryClient` (staleTime 30s,
 * retry 1 em query; `mutations:{retry:0}`).
 *
 * Convencoes de queryKey: prefixo `"alertas"` + recurso + parametros que afetam o
 * resultado. O historico inclui o OBJETO `filtros` na key: trocar pagina (offset)
 * ou qualquer filtro e uma query distinta no cache — refetch automatico ao mudar.
 */

/** Historico de alertas, parametrizado por `filtros` (paginacao por offset). */
export function useAlertas(
  filtros: FiltrosAlertas,
): UseQueryResult<AlertaHistorico[]> {
  return useQuery({
    queryKey: ["alertas", "historico", filtros],
    queryFn: () => buscarAlertas(filtros),
  });
}

/** Empresas configuraveis pelo escopo (`configurar_alertas`) do usuario. */
export function useEmpresasAlertas(): UseQueryResult<EmpresaAlertas[]> {
  return useQuery({
    queryKey: ["alertas", "empresas"],
    queryFn: buscarEmpresasAlertas,
  });
}

/** Argumento da mutacao de reagendamento: o outbox-alvo + a justificativa. */
interface ReagendarArgs {
  outboxId: string;
  justificativa: string;
}

/**
 * Reagenda (reabre) uma notificacao em dead_letter para novo envio.
 *
 * NO SUCESSO, invalida o historico (`["alertas","historico"]` — prefixo, abrange
 * todas as paginas/filtros): a linha reaberta troca de status no proximo fetch.
 *
 * NAO seta `retry` aqui: `criarQueryClient` ja aplica `mutations:{retry:0}` — uma
 * acao de EFEITO EXTERNO (reenviar notificacao) nao retenta as cegas.
 */
export function useReagendar(): UseMutationResult<
  ReagendarResposta,
  unknown,
  ReagendarArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ outboxId, justificativa }: ReagendarArgs) =>
      reagendarNotificacao(outboxId, { justificativa }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["alertas", "historico"],
      }),
  });
}
